<div class="ai-visibility-dashboard">
  <div class="ai-visibility-header">
    <h3 class="ai-visibility-title">AI Visibility</h3>
    @if (!hideReloadButton && !isLLMComparisonMetricsLoading && !llmComparisonMetricsTaskId && llmComparisonData && llmComparisonData.length > 0) {
      <button mat-icon-button 
              class="reload-button" 
              (click)="onReloadClick()"
              matTooltip="Reload AI Visibility metrics"
              aria-label="Reload AI Visibility metrics">
        <mat-icon>refresh</mat-icon>
      </button>
    }
  </div>
  
  <!-- Progress/Loading State - always visible when loading -->
  @if (showInlineProgress && (isLLMComparisonMetricsLoading || llmComparisonMetricsTaskId)) {
    <div class="llm-metrics-progress-container">
      <div class="progress-header">
        <mat-spinner diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
        <span class="progress-text">{{ getStatusLabel() }}</span>
        @if (llmComparisonMetricsTaskId) {
          <button mat-icon-button 
                  class="stop-button" 
                  (click)="onStopClick()"
                  matTooltip="Stop calculation"
                  aria-label="Stop calculation">
            <mat-icon>stop</mat-icon>
          </button>
        }
      </div>
      @if (llmComparisonMetricsProgress) {
        <div class="progress-bar-container">
          <div class="progress-bar">
            <div class="progress-bar-fill" [style.width.%]="llmComparisonMetricsProgress.progress_percentage || 0"></div>
          </div>
          <div class="progress-percentage">{{ (llmComparisonMetricsProgress.progress_percentage || 0).toFixed(0) }}%</div>
        </div>
        <div class="progress-message">
          {{ getStatusDetailText() }}
          @if (getStatusMessages().length > 0) {
            <div class="progress-status-messages" aria-live="polite">
              @for (msg of getStatusMessages(); track msg) {
                <div class="progress-status-line">{{ msg }}</div>
              }
            </div>
          }
          <div class="time-estimate">* Estimated: 3-5 minutes total</div>
        </div>
      } @else {
        <div class="progress-message">
          Pending
          <div class="time-estimate">* Estimated: 3-5 minutes total</div>
        </div>
      }
    </div>
  }
  
  @if (llmComparisonData && llmComparisonData.length > 0) {
  @let overallMetrics = getOverallLLMMetrics();
  @let visibilityCategory = getVisibilityCategory(overallMetrics.aiVisibility);
  
  <!-- Main Gauge Section (visible while loading so your-brand score stays on screen) -->
  <div class="gauge-section" [class.gauge-while-loading]="isLLMComparisonMetricsLoading || llmComparisonMetricsTaskId">
    <div class="gauge-container-wrapper">
      <div class="gauge-container">
        <!-- @if (overallMetrics.industryAverage > 0) {
          <div class="industry-avg-label-top">Industry Avg: {{ overallMetrics.industryAverage | number:'1.0-0' }}</div>
        } -->
        <div class="gauge-wrapper">
          <app-semi-circle 
            [score]="{ domain: [0, 100], value: overallMetrics.aiVisibility }"
            [min]="0"
            [max]="100"
            [colorScheme]="{ domain: ['#1976d2', '#42a5f5'] }">
          </app-semi-circle>
          <div class="gauge-score-overlay">
            <div class="score-value">{{ overallMetrics.aiVisibility | number:'1.0-0' }}<span class="score-divider">/100</span></div>
            <div class="gauge-additional-info">
              <div class="gauge-category" [class]="'category-' + visibilityCategory.toLowerCase()">{{ visibilityCategory }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Description Box -->
    <div class="description-box">
      <p>{{ getVisibilityDescription(overallMetrics.aiVisibility) }}</p>
    </div>
  </div>

  <!-- Metrics Cards Row -->
  <div class="metrics-container">
    <!-- Monthly Audience - Full Width -->
    <div class="metric-card-large metric-full-width">
      <div class="metric-header">
        <span class="metric-label">Monthly Audience</span>
        <!-- <mat-icon class="info-icon" [matTooltip]="getExplanation('monthly_audience') || 'Estimated monthly search audience'">info</mat-icon> -->
      </div>
      <div class="metric-value-row">
        <span class="metric-value-big">{{ formatNumber(overallMetrics.monthlyAudience) }}</span>
        @if (getMonthlyAudienceChange() !== 0) {
          <span class="metric-change" [class.positive]="getMonthlyAudienceChange() > 0" [class.negative]="getMonthlyAudienceChange() < 0">
            {{ getMonthlyAudienceChange() > 0 ? '+' : '' }}{{ formatNumber(getMonthlyAudienceChange()) }}
          </span>
        }
      </div>
    </div>

    <!-- Mentions and Cited Pages - 50% width each -->
    <div class="metrics-row-bottom">
      <!-- Mentions -->
      <div class="metric-card-large metric-half-width">
        <div class="metric-header">
          <span class="metric-label">Mentions</span>
          <!-- <mat-icon class="info-icon" [matTooltip]="getExplanation('total_mentions') || 'Total mentions across AI platforms'">info</mat-icon> -->
        </div>
        <div class="metric-value-row">
          <span class="metric-value-big">{{ formatNumber(overallMetrics.mentions) }}</span>
          @if (getMentionsChange() !== 0) {
            <span class="metric-change" [class.positive]="getMentionsChange() > 0" [class.negative]="getMentionsChange() < 0">
              {{ getMentionsChange() > 0 ? '+' : '' }}{{ formatNumber(getMentionsChange()) }}
            </span>
          }
        </div>
      </div>

      <!-- Cited Pages -->
      <div class="metric-card-large metric-half-width" style="cursor: pointer" (click)="onCitedPagesClick()">
        <div class="metric-header">
          <span class="metric-label">Cited Pages</span>
          <!-- <mat-icon class="info-icon" [matTooltip]="getExplanation('total_cited_pages') || 'Total unique pages cited by AI platforms'">info</mat-icon> -->
        </div>
        <div class="metric-value-row">
          <span class="metric-value-big" style="border-bottom: 1px solid;">{{ formatNumber(overallMetrics.citedPages) }}</span>
          @if (getCitedPagesChange() !== 0) {
            <span class="metric-change" [class.positive]="getCitedPagesChange() > 0" [class.negative]="getCitedPagesChange() < 0">
              {{ getCitedPagesChange() > 0 ? '+' : '' }}{{ formatNumber(getCitedPagesChange()) }}
            </span>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Platform Breakdown (only show cards for platforms that have data) -->
  <div class="platform-breakdown-section">
    <div class="platform-list">
      @if (hasPlatformData('chatgpt')) {
      <div class="platform-item" style="cursor: pointer;" (click)="onChatGPTClick()">
        <div class="platform-left">
          <div class="platform-icon chatgpt-icon">
            <img src="/images/logo/chatgpt.png" alt="ChatGPT" />
          </div>
          <div class="platform-name">
            Chat GPT
          </div>
        </div>
        <div class="platform-value">{{ formatNumber(getChatGPTMetric('mentions')) }}</div>
      </div>
      }
      
      @if (hasPlatformData('ai_overview')) {
      <div class="platform-item" style="cursor: pointer;" (click)="onAIOverviewClick()">
        <div class="platform-left">
          <div class="platform-icon google-icon">
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
          <div class="platform-name">
            AI Overview
          </div>
        </div>
        <div class="platform-value">{{ formatNumber(getAIOverviewMetric('mentions')) }}</div>
      </div>
      }
      
      @if (hasPlatformData('ai_mode')) {
      <div class="platform-item" style="cursor: pointer;" (click)="onAIModeClick()">
        <div class="platform-left">
          <div class="platform-icon google-icon">
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07	H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93ଲ2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64ଲ3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07ଲ3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
          <div class="platform-name">
            AI Mode
          </div>
        </div>
        <div class="platform-value">{{ formatNumber(getAIModeMetric('mentions')) }}</div>
      </div>
      }
      
      @if (hasPlatformData('gemini')) {
      <div class="platform-item" style="cursor: pointer;" (click)="onGeminiClick()">
        <div class="platform-left">
          <div class="platform-icon gemini-icon">
            <img src="/images/logo/gemini.jpg" alt="Gemini" />
          </div>
          <div class="platform-name">
            Gemini
          </div>
        </div>
        <div class="platform-value">{{ formatNumber(getGeminiMetric('mentions')) }}</div>
      </div>
      }
    </div>
  </div>
  } @else if (!isLLMComparisonMetricsLoading && !llmComparisonMetricsTaskId) {
    <!-- No data and not loading and no active task - show placeholder -->
    <div class="no-data-message">
      <p>AI Visibility metrics will be calculated after the main analysis completes.</p>
      <p class="sub-message">This process takes 3-5 minutes and runs in the background.</p>
    </div>
  }
</div>
